import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function LoginPage() {
  const { login, register, googleLogin, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('signin'); // 'signin' | 'signup'
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    if (isAuthenticated) navigate('/', { replace: true });
  }, [isAuthenticated, navigate]);

  // Google Sign-In callback
  const handleGoogleCredential = useCallback(async (response) => {
    setError('');
    setIsSubmitting(true);
    try {
      await googleLogin(response.credential);
      navigate('/');
    } catch (err) {
      setError(err?.response?.data?.error || 'Google sign-in failed');
    }
    setIsSubmitting(false);
  }, [googleLogin, navigate]);

  // Initialize Google Sign-In (wait for script to load)
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    const renderGoogleButton = () => {
      if (typeof window.google === 'undefined') return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
      });
      const btn = document.getElementById('google-signin-btn');
      if (btn) {
        btn.innerHTML = '';
        window.google.accounts.id.renderButton(btn, {
          theme: 'outline',
          size: 'large',
          width: 320,
          text: 'continue_with',
        });
      }
    };

    if (typeof window.google !== 'undefined') {
      renderGoogleButton();
    } else {
      // Script hasn't loaded yet — poll until ready
      const interval = setInterval(() => {
        if (typeof window.google !== 'undefined') {
          clearInterval(interval);
          renderGoogleButton();
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, [handleGoogleCredential, view]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      if (view === 'signin') {
        await login(email, password);
      } else {
        await register(name, email, password);
      }
      navigate('/');
    } catch (err) {
      setError(err?.response?.data?.error || 'Authentication failed');
    }
    setIsSubmitting(false);
  };

  const switchView = () => {
    setView(v => v === 'signin' ? 'signup' : 'signin');
    setError('');
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoContainer}>
          <img src="/assets/ayni_logo.png" alt="AYNI" style={styles.logo} />
        </div>

        {/* Heading */}
        <h1 style={styles.heading}>{view === 'signin' ? 'Sign in' : 'Create account'}</h1>
        <p style={styles.subheading}>to continue to AYNI</p>

        {/* Google Sign-In */}
        <div id="google-signin-btn" style={styles.googleContainer}></div>

        {/* Divider */}
        <div style={styles.divider}>
          <div style={styles.dividerLine}></div>
          <span style={styles.dividerText}>or</span>
          <div style={styles.dividerLine}></div>
        </div>

        {/* Error */}
        {error && <div style={styles.error}>{error}</div>}

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          {view === 'signup' && (
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Full name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                style={styles.input}
              />
            </div>
          )}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Email address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={styles.input}
            />
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              style={styles.input}
            />
          </div>
          <button type="submit" disabled={isSubmitting} style={styles.submitButton}>
            {isSubmitting ? 'Please wait...' : (view === 'signin' ? 'Sign In' : 'Create Account')}
          </button>
        </form>

        {/* Toggle link */}
        <p style={styles.toggleText}>
          {view === 'signin' ? "Don't have an account? " : 'Already have an account? '}
          <button onClick={switchView} style={styles.toggleLink}>
            {view === 'signin' ? 'Sign up' : 'Sign in'}
          </button>
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: 'var(--bg-page)',
    fontFamily: "'Inter', sans-serif",
  },
  card: {
    backgroundColor: 'var(--bg-card)',
    borderRadius: '12px',
    padding: '40px',
    width: '400px',
    maxWidth: '90vw',
    boxShadow: 'var(--shadow-card, 0 1px 3px rgba(0,0,0,0.1))',
  },
  logoContainer: {
    textAlign: 'center',
    marginBottom: '24px',
  },
  logo: {
    height: '48px',
    objectFit: 'contain',
  },
  heading: {
    fontSize: '22px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    margin: '0 0 4px 0',
  },
  subheading: {
    fontSize: '14px',
    color: 'var(--text-muted)',
    margin: '0 0 24px 0',
  },
  googleContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: 'var(--border-default, #e5e7eb)',
  },
  dividerText: {
    color: 'var(--text-muted)',
    fontSize: '13px',
  },
  error: {
    backgroundColor: 'var(--badge-red-bg, #fef2f2)',
    color: 'var(--badge-red-text, #dc2626)',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '13px',
    marginBottom: '16px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    fontSize: '13px',
    fontWeight: '500',
    color: 'var(--text-primary)',
  },
  input: {
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '14px',
    border: '1px solid var(--border-input, #d1d5db)',
    backgroundColor: 'var(--bg-input, #fff)',
    color: 'var(--text-primary)',
    fontFamily: "'Inter', sans-serif",
    outline: 'none',
  },
  submitButton: {
    padding: '12px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    border: 'none',
    backgroundColor: 'var(--color-primary, #2563eb)',
    color: '#ffffff',
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
    marginTop: '4px',
  },
  toggleText: {
    textAlign: 'center',
    fontSize: '13px',
    color: 'var(--text-muted)',
    marginTop: '20px',
  },
  toggleLink: {
    background: 'none',
    border: 'none',
    color: 'var(--color-primary, #2563eb)',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    padding: 0,
    fontFamily: "'Inter', sans-serif",
  },
};

export default LoginPage;
